import { useLayoutEffect } from 'react';

const BEFORE_ROLE_VIEW_SWITCH = 'cpq-role-view-before-switch';
const BEFORE_PENDING_SAVE_LEAVE = 'cpq-before-pending-save-leave';
type GuardDetail = { blockedEditors: string[] };

/** Keep an unresolved command in its owning page until it can be reconciled. */
export function usePendingSaveLeaveGuard(
  pending: boolean,
  message = '设备保存结果尚未确认，请先处理当前保存，再离开设备页。',
) {
  useLayoutEffect(() => {
    if (!pending) return;
    const guard = (event: Event) => {
      event.preventDefault();
      if (event instanceof CustomEvent && Array.isArray(event.detail?.messages))
        event.detail.messages.push(message);
    };
    window.addEventListener(BEFORE_PENDING_SAVE_LEAVE, guard);
    return () => window.removeEventListener(BEFORE_PENDING_SAVE_LEAVE, guard);
  }, [pending, message]);
}

export function requestPendingSaveLeaveCheck() {
  const event = new CustomEvent(BEFORE_PENDING_SAVE_LEAVE, {
    cancelable: true,
    detail: { messages: [] as string[] },
  });
  if (!window.dispatchEvent(event))
    throw new Error(
      event.detail.messages[0] ?? '保存结果尚未确认，请先处理当前保存。',
    );
}

/** Editors register their actual page state; a role switch never inspects the DOM or caches their sensitive form. */
export function useRoleViewEditorGuard(
  open: boolean,
  editor:
    | '设备'
    | '套餐'
    | '方案'
    | '报价编辑'
    | '角色权限'
    | '成员授权'
    | '用户与权限',
) {
  useLayoutEffect(() => {
    if (!open) return;
    const guard = (event: Event) => {
      const before = event as CustomEvent<GuardDetail>;
      before.preventDefault();
      before.detail.blockedEditors.push(editor);
    };
    window.addEventListener(BEFORE_ROLE_VIEW_SWITCH, guard);
    return () => window.removeEventListener(BEFORE_ROLE_VIEW_SWITCH, guard);
  }, [open, editor]);
}

export function requestRoleViewSwitch(): {
  allowed: boolean;
  blockedEditors: string[];
} {
  const event = new CustomEvent<GuardDetail>(BEFORE_ROLE_VIEW_SWITCH, {
    cancelable: true,
    detail: { blockedEditors: [] },
  });
  const allowed = window.dispatchEvent(event);
  return { allowed, blockedEditors: [...new Set(event.detail.blockedEditors)] };
}
